import React, { useEffect, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  TrendUp, CurrencyInr, Package, UsersThree, Receipt, Coins,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";

const Stat = ({ label, value, icon: Icon, accent = false, testId }) => (
  <div data-testid={testId} className="kpi-card rounded-xl p-5 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <span className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">{label}</span>
      <div className={`h-9 w-9 grid place-items-center rounded-lg ${accent ? "bg-primary/15 border border-primary/30 text-gold" : "bg-accent text-muted-foreground"}`}>
        <Icon size={18} weight="duotone" />
      </div>
    </div>
    <div className="font-display text-2xl tabular-nums tracking-tight">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/summary")
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="dashboard-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">
            Welcome back, {user?.name?.split(" ")[0] || "Jeweller"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Here is your store at a glance — today, this month, and the trend so far.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
      </header>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map((i) => <div key={i} className="h-28 rounded-xl shimmer" />)}
        </div>
      ) : (
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat testId="kpi-today-sales" label="Today's Sales" value={inr(data?.today_sales)} icon={CurrencyInr} accent />
            <Stat testId="kpi-month-sales" label="Monthly Sales" value={inr(data?.month_sales)} icon={TrendUp} />
            <Stat testId="kpi-bills" label="Today's Bills" value={data?.today_bills ?? 0} icon={Receipt} />
            <Stat testId="kpi-receivables" label="Receivables" value={inr(data?.receivables)} icon={Coins} />
            <Stat testId="kpi-customers" label="Customers" value={data?.customers ?? 0} icon={UsersThree} />
            <Stat testId="kpi-products" label="SKUs in Stock" value={data?.products ?? 0} icon={Package} />
            <Stat
              testId="kpi-gold-stock"
              label="Gold Stock"
              value={grams(data?.stock_by_metal?.find((m) => m.metal === "Gold")?.weight)}
              icon={Coins}
              accent
            />
            <Stat
              testId="kpi-silver-stock"
              label="Silver Stock"
              value={grams(data?.stock_by_metal?.find((m) => m.metal === "Silver")?.weight)}
              icon={Coins}
            />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 kpi-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Sales Trend</div>
                  <h3 className="font-display text-xl mt-1">Last 14 days</h3>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data?.sales_trend || []} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(46 65% 52%)" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="hsl(46 65% 52%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 4% 16%)" />
                    <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} fontSize={11} stroke="hsl(240 5% 65%)" />
                    <YAxis fontSize={11} stroke="hsl(240 5% 65%)" />
                    <Tooltip
                      contentStyle={{ background: "hsl(240 5% 7%)", border: "1px solid hsl(240 4% 16%)", borderRadius: 8 }}
                      labelStyle={{ color: "hsl(0 0% 98%)" }}
                      formatter={(v) => inr(v)}
                    />
                    <Area type="monotone" dataKey="amount" stroke="hsl(46 65% 52%)" strokeWidth={2} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="kpi-card rounded-xl p-6">
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Stock by Metal</div>
                <h3 className="font-display text-xl mt-1">Weight (g)</h3>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.stock_by_metal || []} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 4% 16%)" />
                    <XAxis dataKey="metal" fontSize={11} stroke="hsl(240 5% 65%)" />
                    <YAxis fontSize={11} stroke="hsl(240 5% 65%)" />
                    <Tooltip
                      contentStyle={{ background: "hsl(240 5% 7%)", border: "1px solid hsl(240 4% 16%)", borderRadius: 8 }}
                      labelStyle={{ color: "hsl(0 0% 98%)" }}
                      formatter={(v) => `${v.toFixed(3)} g`}
                    />
                    <Bar dataKey="weight" fill="hsl(46 65% 52%)" radius={[6,6,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
